import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
    Calendar as CalendarIcon, Plus, Phone, Trash2, Edit, CheckCircle2, XCircle,
    Clock, AlertCircle, ExternalLink, Inbox, Mail, MapPin, RotateCw, Flag, Users, X
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth } from "../hooks/useAuth";
import {
    fetchCalendarEvents, updateCalendarEvent, deleteCalendarEvent
} from "../services/crmServices/calendarService";
import { fetchUsers } from "../services/crmServices/userService";
import { ScheduleCallbackModal } from "../components/calendar/ScheduleCallbackModal";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useTimeOffset } from "../hooks/useTimeOffset";
import "./Calendar.css";

// ── Theme tokens (matches Calendar.css) ──────────────────────────────────
const TYPE_META = {
    callback:  { label: "Callback",  color: "#2563EB", soft: "#EFF6FF", icon: "📞" },
    follow_up: { label: "Follow-up", color: "#7C3AED", soft: "#F5F3FF", icon: "📅" },
    meeting:   { label: "Meeting",   color: "#059669", soft: "#ECFDF5", icon: "💼" },
    task:      { label: "Task",      color: "#D97706", soft: "#FFFBEB", icon: "✅" }
};

const STATUS_META = {
    scheduled:   { label: "Scheduled",   color: "#2563EB", soft: "bg-blue-50 text-blue-700 border-blue-200" },
    done:        { label: "Done",        color: "#059669", soft: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    missed:      { label: "Missed",      color: "#DC2626", soft: "bg-red-50 text-red-700 border-red-200" },
    rescheduled: { label: "Rescheduled", color: "#D97706", soft: "bg-amber-50 text-amber-700 border-amber-200" },
    cancelled:   { label: "Cancelled",   color: "#6B7280", soft: "bg-gray-100 text-gray-600 border-gray-200" }
};

const PRIORITY_META = {
    low:    { label: "Low",    color: "#9CA3AF" },
    normal: { label: "Normal", color: "#6B7280" },
    high:   { label: "High",   color: "#F59E0B" },
    urgent: { label: "Urgent", color: "#DC2626" }
};

// Translate a CRM event into FullCalendar style props
function getEventStyles(e) {
    const type = TYPE_META[e.type] || TYPE_META.callback;
    let backgroundColor = type.color;
    let borderColor = type.color;
    let textColor = "#FFFFFF";

    if (e.status === "done") {
        backgroundColor = "#D1FAE5";
        borderColor = "#10B981";
        textColor = "#065F46";
    } else if (e.status === "missed") {
        backgroundColor = "#FEE2E2";
        borderColor = "#EF4444";
        textColor = "#991B1B";
    } else if (e.status === "cancelled") {
        backgroundColor = "#F3F4F6";
        borderColor = "#9CA3AF";
        textColor = "#6B7280";
    } else if (e.priority === "urgent") {
        borderColor = "#DC2626";
    }

    return { backgroundColor, borderColor, textColor };
}

export function Calendar() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const calendarRef = useRef(null);

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editEvent, setEditEvent] = useState(null);
    const [quickStart, setQuickStart] = useState(null);
    const [detailEvent, setDetailEvent] = useState(null);
    const [activeFilter, setActiveFilter] = useState("all"); // all | scheduled | done | missed

    // Manager/admin agent picker
    const role = user?.position?.role;
    const canPickAgent = role === "admin" || role === "floorManager" || role === "deskManager";
    const [users, setUsers] = useState([]); // managed users for the picker
    const [selectedUserId, setSelectedUserId] = useState(""); // "" = everyone they can see

    // Personal time override (handles wrong system clocks)
    const { offsetMs, now: userNow, setCurrentTime, reset: resetTimeOffset } = useTimeOffset();
    // Force the FullCalendar 'now' indicator to recompute when offset changes
    const [nowKey, setNowKey] = useState(0);
    useEffect(() => { setNowKey((k) => k + 1); }, [offsetMs]);

    // ── Load events for the currently visible range ──────────────────────
    const loadEvents = useCallback(async (from, to) => {
        setLoading(true);
        try {
            const res = await fetchCalendarEvents({
                from: from?.toISOString(),
                to: to?.toISOString(),
                ownerId: selectedUserId || undefined
            });
            setEvents(res?.data || []);
        } catch (err) {
            toast.error("Failed to load events");
        } finally {
            setLoading(false);
        }
    }, [selectedUserId]);

    useEffect(() => {
        const from = new Date();
        from.setDate(from.getDate() - 14);
        const to = new Date();
        to.setDate(to.getDate() + 90);
        loadEvents(from, to);
    }, [loadEvents]);

    // Load the list of users this manager/admin can pick from
    useEffect(() => {
        if (!canPickAgent) return;
        (async () => {
            try {
                const res = await fetchUsers();
                const list = res?.data || [];
                setUsers(Array.isArray(list) ? list : []);
            } catch {
                // silent
            }
        })();
    }, [canPickAgent]);

    // ── Apply filter + transform for FullCalendar ────────────────────────
    const filteredEvents = useMemo(() => {
        if (activeFilter === "all") return events;
        return events.filter((e) => e.status === activeFilter);
    }, [events, activeFilter]);

    const calendarEvents = filteredEvents.map((e) => {
        const styles = getEventStyles(e);
        return {
            id: e._id,
            title: e.title,
            start: e.startAt,
            end: new Date(new Date(e.startAt).getTime() + (e.durationMinutes || 15) * 60 * 1000),
            ...styles,
            classNames: [
                e.status === "done" ? "crm-event-done" : "",
                e.status === "cancelled" ? "crm-event-cancelled" : "",
                e.priority === "urgent" ? "crm-event-urgent" : ""
            ].filter(Boolean),
            extendedProps: { raw: e }
        };
    });

    // ── Counts (entire range) ────────────────────────────────────────────
    const counts = useMemo(() => events.reduce((acc, e) => {
        acc.total++;
        acc[e.status] = (acc[e.status] || 0) + 1;
        return acc;
    }, { total: 0 }), [events]);

    // ── Upcoming today + tomorrow ────────────────────────────────────────
    const upcoming = useMemo(() => {
        const now = new Date();
        return events
            .filter((e) => e.status === "scheduled" && new Date(e.startAt) >= now)
            .sort((a, b) => new Date(a.startAt) - new Date(b.startAt))
            .slice(0, 6);
    }, [events]);

    // ── Handlers ─────────────────────────────────────────────────────────
    const handleDateSelect = (selectInfo) => {
        setQuickStart(selectInfo.start);
        setEditEvent(null);
        setShowModal(true);
        selectInfo.view.calendar.unselect();
    };

    const handleEventClick = (info) => {
        setDetailEvent(info.event.extendedProps.raw);
    };

    const handleEventDrop = async (info) => {
        const ev = info.event.extendedProps.raw;
        try {
            const res = await updateCalendarEvent(ev._id, { startAt: info.event.start.toISOString() });
            setEvents((prev) => prev.map((e) => e._id === ev._id ? res.data : e));
            toast.success("Event rescheduled");
        } catch {
            toast.error("Failed to reschedule");
            info.revert();
        }
    };

    const handleEventResize = async (info) => {
        const ev = info.event.extendedProps.raw;
        const minutes = Math.max(1, Math.round((info.event.end - info.event.start) / 60000));
        try {
            const res = await updateCalendarEvent(ev._id, { durationMinutes: minutes });
            setEvents((prev) => prev.map((e) => e._id === ev._id ? res.data : e));
            toast.success("Duration updated");
        } catch {
            toast.error("Failed to update duration");
            info.revert();
        }
    };

    const handleSaved = (saved) => {
        if (!saved) return;
        setEvents((prev) => {
            const exists = prev.find((e) => e._id === saved._id);
            if (exists) return prev.map((e) => e._id === saved._id ? saved : e);
            return [...prev, saved];
        });
        setDetailEvent(null);
    };

    const handleDelete = async (eventId) => {
        const confirm = await Swal.fire({
            title: "Delete event?",
            text: "This cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            confirmButtonText: "Delete"
        });
        if (!confirm.isConfirmed) return;
        try {
            await deleteCalendarEvent(eventId);
            setEvents((prev) => prev.filter((e) => e._id !== eventId));
            setDetailEvent(null);
            toast.success("Event deleted");
        } catch {
            toast.error("Failed to delete");
        }
    };

    const handleMarkStatus = async (event, status) => {
        try {
            const res = await updateCalendarEvent(event._id, { status });
            setEvents((prev) => prev.map((e) => e._id === event._id ? res.data : e));
            setDetailEvent(res.data);
            toast.success(`Marked as ${STATUS_META[status]?.label.toLowerCase() || status}`);
        } catch {
            toast.error("Failed to update");
        }
    };

    const handleCallLead = (lead) => {
        if (!lead?.phone) {
            toast.error("No phone on this lead");
            return;
        }
        window.location.href = `tel:${lead.phone}`;
    };

    const goToLead = (leadId) => {
        if (!leadId) return;
        navigate(`/leads/edit/${leadId}`);
    };

    // ── Custom event renderer (small but rich) ───────────────────────────
    const renderEventContent = (eventInfo) => {
        const raw = eventInfo.event.extendedProps.raw;
        const type = TYPE_META[raw.type] || TYPE_META.callback;
        const priority = PRIORITY_META[raw.priority] || PRIORITY_META.normal;
        const isList = eventInfo.view.type === "listWeek";
        const isMonth = eventInfo.view.type === "dayGridMonth";

        const time = eventInfo.timeText;

        if (isList) {
            return (
                <div className="flex items-center gap-2">
                    <span>{type.icon}</span>
                    <span className="font-medium">{eventInfo.event.title}</span>
                    {raw.leadSnapshot?.firstName && (
                        <span className="text-xs text-gray-500">
                            · {raw.leadSnapshot.firstName} {raw.leadSnapshot.lastName || ""}
                        </span>
                    )}
                </div>
            );
        }

        if (isMonth) {
            return (
                <div className="crm-event-content">
                    <div className="crm-event-row">
                        <span className="crm-event-time">{time}</span>
                        <span className="crm-event-title">{eventInfo.event.title}</span>
                    </div>
                </div>
            );
        }

        // Week / day view
        const ownerName = canPickAgent && !selectedUserId && raw.userId
            ? `${raw.userId.firstName || ""} ${raw.userId.lastName || ""}`.trim()
            : "";

        return (
            <div className="crm-event-content">
                <div className="crm-event-row">
                    <span className="crm-event-time">{time}</span>
                    {raw.priority === "urgent" && <Flag className="h-2.5 w-2.5" />}
                </div>
                <div className="crm-event-title">{type.icon} {eventInfo.event.title}</div>
                {(raw.leadSnapshot?.firstName || ownerName) && (
                    <div className="crm-event-meta">
                        {raw.leadSnapshot?.firstName && (
                            <>{raw.leadSnapshot.firstName} {raw.leadSnapshot.lastName || ""}</>
                        )}
                        {ownerName && (
                            <span style={{ opacity: 0.7 }}>
                                {raw.leadSnapshot?.firstName ? " · " : ""}@ {ownerName}
                            </span>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Compute role label, accounting for any active agent filter
    const selectedUser = useMemo(
        () => users.find((u) => String(u._id) === String(selectedUserId)) || null,
        [users, selectedUserId]
    );
    const roleLabel = role === "agent"
        ? "Your callbacks, follow-ups and tasks"
        : selectedUser
            ? `Viewing: ${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim()
            : "Team callbacks and scheduled events";

    return (
        <div className="container mx-auto px-4 sm:px-6 py-4">
            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm">
                        <CalendarIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 tracking-tight">Calendar</h1>
                        <p className="text-xs text-gray-500">{roleLabel}</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {/* Agent picker (admin / floor mgr / desk mgr only) */}
                    {canPickAgent && (
                        <UserPicker
                            users={users}
                            value={selectedUserId}
                            onChange={setSelectedUserId}
                            currentUserId={user?._id}
                        />
                    )}
                    <Button
                        onClick={() => { setEditEvent(null); setQuickStart(null); setShowModal(true); }}
                        className="bg-gray-900 hover:bg-black text-white shadow-sm"
                        size="sm"
                    >
                        <Plus className="h-4 w-4 mr-1.5" />
                        New Event
                    </Button>
                </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-5">
                <StatPill label="Total" value={counts.total} active={activeFilter === "all"} onClick={() => setActiveFilter("all")} color="gray" />
                <StatPill label="Scheduled" value={counts.scheduled || 0} active={activeFilter === "scheduled"} onClick={() => setActiveFilter("scheduled")} color="blue" />
                <StatPill label="Done" value={counts.done || 0} active={activeFilter === "done"} onClick={() => setActiveFilter("done")} color="emerald" />
                <StatPill label="Missed" value={counts.missed || 0} active={activeFilter === "missed"} onClick={() => setActiveFilter("missed")} color="red" />
                <StatPill label="Cancelled" value={counts.cancelled || 0} active={activeFilter === "cancelled"} onClick={() => setActiveFilter("cancelled")} color="gray-soft" />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-4">
                {/* Main calendar */}
                <Card className="border-gray-200 shadow-sm overflow-hidden">
                    <CardContent className="p-3 md:p-4">
                        <div className="crm-calendar-wrapper">
                            <FullCalendar
                                key={`fc-${nowKey}`}
                                ref={calendarRef}
                                plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                                initialView="timeGridWeek"
                                now={offsetMs !== 0 ? userNow() : undefined}
                                headerToolbar={{
                                    left: "prev,next today",
                                    center: "title",
                                    right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek"
                                }}
                                buttonText={{
                                    today: "Today",
                                    month: "Month",
                                    week: "Week",
                                    day: "Day",
                                    list: "Agenda"
                                }}
                                views={{
                                    listWeek: { buttonText: "Agenda" }
                                }}
                                nowIndicator={true}
                                editable={true}
                                droppable={false}
                                selectable={true}
                                selectMirror={true}
                                select={handleDateSelect}
                                eventClick={handleEventClick}
                                eventDrop={handleEventDrop}
                                eventResize={handleEventResize}
                                eventContent={renderEventContent}
                                events={calendarEvents}
                                height="auto"
                                slotMinTime="06:00:00"
                                slotMaxTime="23:00:00"
                                firstDay={1}
                                allDaySlot={false}
                                dayMaxEvents={3}
                                expandRows={true}
                                stickyHeaderDates={true}
                                eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
                                slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
                                dayHeaderFormat={{ weekday: "short", day: "2-digit" }}
                                datesSet={(arg) => {
                                    const from = new Date(arg.start);
                                    from.setDate(from.getDate() - 1);
                                    const to = new Date(arg.end);
                                    to.setDate(to.getDate() + 1);
                                    loadEvents(from, to);
                                }}
                            />
                            {loading && (
                                <div className="text-[10px] text-gray-400 mt-1.5 text-right">
                                    Loading...
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Right sidebar — upcoming + legend */}
                <div className="space-y-4">
                    {/* Upcoming */}
                    <Card className="border-gray-200 shadow-sm">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
                                    <Clock className="h-3.5 w-3.5 text-gray-500" />
                                    Upcoming
                                </h3>
                                <span className="text-[10px] text-gray-400">{upcoming.length}</span>
                            </div>
                            {upcoming.length === 0 ? (
                                <div className="text-center py-6">
                                    <Inbox className="h-7 w-7 text-gray-300 mx-auto mb-2" />
                                    <p className="text-xs text-gray-500">Nothing scheduled</p>
                                </div>
                            ) : (
                                <ul className="space-y-1.5">
                                    {upcoming.map((ev) => {
                                        const t = TYPE_META[ev.type] || TYPE_META.callback;
                                        const lead = ev.leadSnapshot || {};
                                        return (
                                            <li
                                                key={ev._id}
                                                onClick={() => setDetailEvent(ev)}
                                                className="group p-2 rounded-md hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-200 transition-colors"
                                            >
                                                <div className="flex items-start gap-2">
                                                    <span style={{ backgroundColor: t.color }} className="mt-1 h-2 w-2 rounded-full flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between gap-1">
                                                            <p className="text-xs font-medium text-gray-900 truncate">{ev.title}</p>
                                                            <span className="text-[10px] text-blue-600 font-semibold whitespace-nowrap">
                                                                {formatRelative(ev.startAt)}
                                                            </span>
                                                        </div>
                                                        {(lead.firstName || lead.phone) && (
                                                            <p className="text-[10px] text-gray-500 truncate mt-0.5">
                                                                {[lead.firstName, lead.lastName].filter(Boolean).join(" ")}{lead.phone ? ` · ${lead.phone}` : ""}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </CardContent>
                    </Card>

                    {/* Legend */}
                    <Card className="border-gray-200 shadow-sm">
                        <CardContent className="p-4">
                            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Event Types</h3>
                            <ul className="space-y-2">
                                {Object.entries(TYPE_META).map(([key, t]) => (
                                    <li key={key} className="flex items-center gap-2 text-xs text-gray-700">
                                        <span style={{ backgroundColor: t.color }} className="h-2.5 w-2.5 rounded-sm" />
                                        <span>{t.icon} {t.label}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="border-t border-gray-100 mt-3 pt-3 text-[10px] text-gray-500 leading-relaxed">
                                Tip: drag events to reschedule. Click an empty slot to create. Resize from the bottom edge.
                            </div>
                        </CardContent>
                    </Card>

                    {/* My Current Time — override system clock */}
                    <MyTimeCard
                        offsetMs={offsetMs}
                        nowFn={userNow}
                        onSetTime={setCurrentTime}
                        onReset={resetTimeOffset}
                    />
                </div>
            </div>

            {/* Schedule / Edit modal */}
            <ScheduleCallbackModal
                open={showModal}
                onClose={() => { setShowModal(false); setEditEvent(null); setQuickStart(null); }}
                onSaved={handleSaved}
                existingEvent={editEvent}
                defaultStartAt={quickStart}
            />

            {/* Event detail modal */}
            {detailEvent && (
                <Dialog open={!!detailEvent} onOpenChange={(v) => { if (!v) setDetailEvent(null); }}>
                    <DialogContent className="max-w-lg rounded-xl p-0 overflow-hidden">
                        {/* Header strip */}
                        <div
                            className="px-5 py-4 border-b border-gray-100"
                            style={{
                                background: `linear-gradient(135deg, ${(TYPE_META[detailEvent.type] || TYPE_META.callback).soft} 0%, #FFFFFF 100%)`
                            }}
                        >
                            <DialogHeader className="space-y-0">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 mb-1">
                                            <span style={{ backgroundColor: (TYPE_META[detailEvent.type] || TYPE_META.callback).color }} className="h-2 w-2 rounded-full" />
                                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                                                {(TYPE_META[detailEvent.type] || TYPE_META.callback).label}
                                            </span>
                                            <Badge variant="outline" className={`text-[10px] h-4 px-1.5 ${(STATUS_META[detailEvent.status] || {}).soft || ""}`}>
                                                {(STATUS_META[detailEvent.status] || {}).label || detailEvent.status}
                                            </Badge>
                                            {detailEvent.priority && detailEvent.priority !== "normal" && (
                                                <Badge variant="outline" className="text-[10px] h-4 px-1.5" style={{ color: (PRIORITY_META[detailEvent.priority] || {}).color, borderColor: (PRIORITY_META[detailEvent.priority] || {}).color }}>
                                                    <Flag className="h-2.5 w-2.5 mr-0.5" />
                                                    {(PRIORITY_META[detailEvent.priority] || {}).label}
                                                </Badge>
                                            )}
                                        </div>
                                        <DialogTitle className="text-base font-semibold text-gray-900 truncate">
                                            {detailEvent.title}
                                        </DialogTitle>
                                        <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 flex-wrap">
                                            <Clock className="h-3 w-3" />
                                            {new Date(detailEvent.startAt).toLocaleString(undefined, {
                                                weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
                                            })}
                                            <span className="text-gray-400">·</span>
                                            <span>{detailEvent.durationMinutes || 15} min</span>
                                            {canPickAgent && detailEvent.userId && (detailEvent.userId.firstName || detailEvent.userId.lastName) && (
                                                <>
                                                    <span className="text-gray-400">·</span>
                                                    <span className="inline-flex items-center gap-0.5 text-gray-600">
                                                        <Users className="h-3 w-3" />
                                                        {detailEvent.userId.firstName} {detailEvent.userId.lastName}
                                                    </span>
                                                </>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </DialogHeader>
                        </div>

                        <div className="px-5 py-4 space-y-4">
                            {/* Lead card */}
                            {detailEvent.leadSnapshot && (detailEvent.leadSnapshot.firstName || detailEvent.leadSnapshot.lastName || detailEvent.leadSnapshot.phone) && (
                                <div className="crm-event-detail-card">
                                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Lead</div>
                                    <div className="font-semibold text-sm text-gray-900">
                                        {detailEvent.leadSnapshot.firstName} {detailEvent.leadSnapshot.lastName}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                                        {detailEvent.leadSnapshot.phone && (
                                            <span className="text-xs text-gray-600 flex items-center gap-1">
                                                <Phone className="h-3 w-3" />
                                                {detailEvent.leadSnapshot.phone}
                                            </span>
                                        )}
                                        {detailEvent.leadSnapshot.email && (
                                            <span className="text-xs text-gray-600 flex items-center gap-1">
                                                <Mail className="h-3 w-3" />
                                                <span className="truncate max-w-[180px]">{detailEvent.leadSnapshot.email}</span>
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex gap-1.5 mt-2.5">
                                        {detailEvent.leadSnapshot.phone && (
                                            <Button size="sm" onClick={() => handleCallLead(detailEvent.leadSnapshot)}
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 px-2.5">
                                                <Phone className="h-3 w-3 mr-1" /> Call
                                            </Button>
                                        )}
                                        {detailEvent.leadId && (
                                            <Button size="sm" variant="outline" onClick={() => goToLead(detailEvent.leadId)} className="text-xs h-7 px-2.5">
                                                <ExternalLink className="h-3 w-3 mr-1" /> Open Lead
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Notes */}
                            {detailEvent.notes && (
                                <div>
                                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</div>
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-md p-2.5 border border-gray-100">
                                        {detailEvent.notes}
                                    </p>
                                </div>
                            )}

                            {/* Status actions */}
                            <div className="border-t border-gray-100 pt-3">
                                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Mark as</div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                                    {detailEvent.status !== "done" && (
                                        <StatusButton onClick={() => handleMarkStatus(detailEvent, "done")} icon={CheckCircle2} label="Done" colorClass="text-emerald-700 border-emerald-300 hover:bg-emerald-50" />
                                    )}
                                    {detailEvent.status !== "missed" && (
                                        <StatusButton onClick={() => handleMarkStatus(detailEvent, "missed")} icon={AlertCircle} label="Missed" colorClass="text-red-700 border-red-300 hover:bg-red-50" />
                                    )}
                                    {detailEvent.status !== "cancelled" && (
                                        <StatusButton onClick={() => handleMarkStatus(detailEvent, "cancelled")} icon={XCircle} label="Cancel" colorClass="text-gray-700 border-gray-300 hover:bg-gray-50" />
                                    )}
                                    {detailEvent.status !== "scheduled" && (
                                        <StatusButton onClick={() => handleMarkStatus(detailEvent, "scheduled")} icon={RotateCw} label="Re-open" colorClass="text-blue-700 border-blue-300 hover:bg-blue-50" />
                                    )}
                                </div>
                            </div>

                            {/* Footer actions */}
                            <div className="flex justify-between pt-2 border-t border-gray-100">
                                <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" onClick={() => handleDelete(detailEvent._id)}>
                                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                                </Button>
                                <Button size="sm" className="bg-gray-900 hover:bg-black text-white" onClick={() => { setEditEvent(detailEvent); setDetailEvent(null); setShowModal(true); }}>
                                    <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

// ── Sub-components ───────────────────────────────────────────────────────
function StatPill({ label, value, active, onClick, color }) {
    const palette = {
        gray:      { bg: "bg-white",      text: "text-gray-700",    border: "border-gray-200", activeBg: "bg-gray-900 text-white border-gray-900" },
        blue:      { bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200", activeBg: "bg-blue-600 text-white border-blue-600" },
        emerald:   { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", activeBg: "bg-emerald-600 text-white border-emerald-600" },
        red:       { bg: "bg-red-50",     text: "text-red-700",     border: "border-red-200", activeBg: "bg-red-600 text-white border-red-600" },
        "gray-soft":{ bg: "bg-gray-50",   text: "text-gray-600",    border: "border-gray-200", activeBg: "bg-gray-600 text-white border-gray-600" }
    }[color] || { bg: "bg-white", text: "text-gray-700", border: "border-gray-200", activeBg: "bg-gray-900 text-white border-gray-900" };

    return (
        <button
            onClick={onClick}
            className={`group flex items-center justify-between px-3 py-2 rounded-lg border transition-all shadow-sm ${
                active ? palette.activeBg : `${palette.bg} ${palette.text} ${palette.border} hover:shadow`
            }`}
        >
            <span className={`text-[10px] font-semibold uppercase tracking-wide ${active ? "" : "opacity-70"}`}>{label}</span>
            <span className="text-base font-bold tabular-nums">{value}</span>
        </button>
    );
}

// Compact, searchable agent picker. Shows "Everyone (Team)" as default; the
// selected agent gets a chip with X to clear.
function UserPicker({ users, value, onChange, currentUserId }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const sorted = useMemo(() => {
        // Sort: self first, then alpha
        const arr = [...(users || [])];
        arr.sort((a, b) => {
            if (String(a._id) === String(currentUserId)) return -1;
            if (String(b._id) === String(currentUserId)) return 1;
            const an = `${a.firstName || ""} ${a.lastName || ""}`.trim().toLowerCase();
            const bn = `${b.firstName || ""} ${b.lastName || ""}`.trim().toLowerCase();
            return an.localeCompare(bn);
        });
        return arr;
    }, [users, currentUserId]);

    const filtered = sorted.filter((u) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
        const email = (u.email || "").toLowerCase();
        return name.includes(q) || email.includes(q);
    });

    const selectedUser = sorted.find((u) => String(u._id) === String(value));

    const roleColor = (r) => ({
        admin: "bg-red-50 text-red-700 border-red-200",
        floorManager: "bg-amber-50 text-amber-700 border-amber-200",
        deskManager: "bg-sky-50 text-sky-700 border-sky-200"
    }[r] || "bg-emerald-50 text-emerald-700 border-emerald-200");

    return (
        <div className="relative">
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 h-9 pl-2.5 pr-2 rounded-md border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors text-xs"
            >
                <Users className="h-3.5 w-3.5 text-gray-500" />
                {selectedUser ? (
                    <>
                        <span className="font-medium text-gray-900 max-w-[140px] truncate">
                            {selectedUser.firstName} {selectedUser.lastName}
                        </span>
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); onChange(""); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onChange(""); } }}
                            className="p-0.5 rounded hover:bg-gray-200 inline-flex"
                            aria-label="Clear filter"
                        >
                            <X className="h-3 w-3 text-gray-500" />
                        </span>
                    </>
                ) : (
                    <span className="text-gray-600 font-medium">Everyone (Team)</span>
                )}
                <svg className="h-3 w-3 text-gray-400 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
            </button>

            {open && (
                <>
                    {/* Click-outside backdrop */}
                    <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 mt-1.5 w-72 z-40 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                        <div className="p-2 border-b border-gray-100">
                            <input
                                autoFocus
                                placeholder="Search agents..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div className="max-h-72 overflow-y-auto">
                            <button
                                onClick={() => { onChange(""); setOpen(false); setSearch(""); }}
                                className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-gray-50 border-b border-gray-100 ${!value ? "bg-blue-50" : ""}`}
                            >
                                <span className="flex items-center gap-2">
                                    <Users className="h-3.5 w-3.5 text-gray-500" />
                                    <span className="font-medium text-gray-900">Everyone (Team)</span>
                                </span>
                                {!value && <CheckCircle2 className="h-3.5 w-3.5 text-blue-600" />}
                            </button>
                            {filtered.length === 0 ? (
                                <div className="px-3 py-6 text-center text-xs text-gray-400">No agents match</div>
                            ) : (
                                filtered.map((u) => {
                                    const isSelf = String(u._id) === String(currentUserId);
                                    const active = String(u._id) === String(value);
                                    const r = u.position?.role;
                                    return (
                                        <button
                                            key={u._id}
                                            onClick={() => { onChange(u._id); setOpen(false); setSearch(""); }}
                                            className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-gray-50 border-b border-gray-50 last:border-0 ${active ? "bg-blue-50" : ""}`}
                                        >
                                            <span className="flex items-center gap-2 min-w-0">
                                                <span className="h-6 w-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                                                    {(u.firstName?.[0] || "?").toUpperCase()}{(u.lastName?.[0] || "").toUpperCase()}
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block font-medium text-gray-900 truncate">
                                                        {u.firstName} {u.lastName}
                                                        {isSelf && <span className="text-[10px] text-gray-400 ml-1">(you)</span>}
                                                    </span>
                                                    {u.email && <span className="block text-[10px] text-gray-500 truncate">{u.email}</span>}
                                                </span>
                                            </span>
                                            <span className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                                                {r && (
                                                    <span className={`text-[9px] px-1.5 py-0.5 rounded border ${roleColor(r)} font-medium uppercase tracking-wide`}>
                                                        {r === "floorManager" ? "Floor" : r === "deskManager" ? "Desk" : r}
                                                    </span>
                                                )}
                                                {active && <CheckCircle2 className="h-3.5 w-3.5 text-blue-600" />}
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function StatusButton({ onClick, icon: Icon, label, colorClass }) {
    return (
        <Button
            size="sm"
            variant="outline"
            onClick={onClick}
            className={`text-xs h-8 ${colorClass}`}
        >
            <Icon className="h-3.5 w-3.5 mr-1" />
            {label}
        </Button>
    );
}

// ── My Current Time card ────────────────────────────────────────────────
function MyTimeCard({ offsetMs, nowFn, onSetTime, onReset }) {
    const [tick, setTick] = useState(0);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");

    // Re-render every second to keep the displayed clock alive
    useEffect(() => {
        const t = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(t);
    }, []);

    const adjusted = nowFn();
    const systemNow = new Date();
    const isOverridden = offsetMs !== 0;
    const formattedAdjusted = adjusted.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    const formattedSystem = systemNow.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    const offsetMinutes = Math.round(offsetMs / 60000);
    const offsetLabel = offsetMinutes === 0
        ? null
        : `${offsetMinutes > 0 ? "+" : ""}${offsetMinutes} min vs. computer`;

    const startEdit = () => {
        const hh = String(adjusted.getHours()).padStart(2, "0");
        const mm = String(adjusted.getMinutes()).padStart(2, "0");
        setDraft(`${hh}:${mm}`);
        setEditing(true);
    };

    const handleSave = () => {
        if (!/^\d{2}:\d{2}$/.test(draft)) {
            toast.error("Use HH:MM format");
            return;
        }
        onSetTime(draft);
        setEditing(false);
        toast.success("Your time has been set");
    };

    return (
        <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-gray-500" />
                        My Current Time
                    </h3>
                    {isOverridden && (
                        <button
                            onClick={onReset}
                            className="text-[10px] text-blue-600 hover:text-blue-800 font-medium"
                            title="Stop overriding"
                        >
                            Reset
                        </button>
                    )}
                </div>

                {/* Big time */}
                <div className={`text-center py-2 rounded-md border ${isOverridden ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-100"}`}>
                    <div className={`text-2xl font-mono font-bold tabular-nums ${isOverridden ? "text-blue-700" : "text-gray-900"}`}>
                        {formattedAdjusted}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                        {isOverridden ? "Override active" : "Using computer clock"}
                    </div>
                </div>

                {/* Override row */}
                {!editing ? (
                    <button
                        onClick={startEdit}
                        className="w-full mt-2 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:text-gray-900 border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
                    >
                        {isOverridden ? "Adjust time" : "Set my time"}
                    </button>
                ) : (
                    <div className="mt-2 flex gap-1.5">
                        <input
                            type="time"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            autoFocus
                            className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 tabular-nums"
                        />
                        <button
                            onClick={handleSave}
                            className="px-2.5 py-1 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Set
                        </button>
                        <button
                            onClick={() => setEditing(false)}
                            className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                        >
                            ✕
                        </button>
                    </div>
                )}

                {isOverridden && (
                    <div className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-500 leading-relaxed">
                        <div>Computer says: <span className="font-mono">{formattedSystem}</span></div>
                        {offsetLabel && <div>{offsetLabel}</div>}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ── Helpers ──────────────────────────────────────────────────────────────
function formatRelative(date) {
    const d = new Date(date);
    const now = new Date();
    const diffMin = Math.round((d - now) / 60000);
    if (diffMin < 0) return "now";
    if (diffMin < 60) return `in ${diffMin}m`;
    if (diffMin < 24 * 60) {
        const h = Math.floor(diffMin / 60);
        return `in ${h}h`;
    }
    const days = Math.floor(diffMin / (60 * 24));
    if (days === 1) return "tomorrow";
    if (days < 7) return d.toLocaleDateString(undefined, { weekday: "short" });
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
